import { Component, EventEmitter, Input, OnChanges, Output, SimpleChange, ChangeDetectionStrategy } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-password-strength',
    styleUrls: ['./password-strength.component.scss'],
    templateUrl: './password-strength.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PasswordStrengthComponent implements OnChanges {
    bar0: string;
    bar1: string;
    bar2: string;
    bar3: string;
    checks = {};

    @Input() public passwordToCheck: string;
    @Input() public showRequirements: boolean;

    @Output() passwordStrength = new EventEmitter<boolean>();

    private colors = ['darkred', 'orangered', 'orange', 'yellowgreen'];

    message: string;
    messageColor: string;

    constructor(public translate: TranslocoService) { }

    ngOnInit() {
        this.checks = {
            lowerLetters: false,
            upperLetters: false,
            numbers: false,
            symbols: false,
            minLength: false
        };
    }

    checkStrength(password: string) {
        let force = 0;

        const lowerLetters = /[a-z]+/.test(password);
        const upperLetters = /[A-Z]+/.test(password);
        const numbers = /[0-9]+/.test(password);
        const symbols = /[\W_]/.test(password);
        const minLength = (password?.length ?? 0) >= 8;

        this.checks = {
            lowerLetters: lowerLetters,
            upperLetters: upperLetters,
            numbers: numbers,
            symbols: symbols,
            minLength: minLength
        };

        let passedMatches = 0;
        for (const [key, value] of Object.entries(this.checks)) {
            passedMatches += value === true ? 1 : 0;
        }

        force += 2 * (password?.length ?? 0) + ((password?.length ?? 0) >= 10 ? 1 : 0);
        force += passedMatches * 10;

        force = (password?.length ?? 0) <= 6 ? Math.min(force, 10) : force;

        if (passedMatches === 1) force = Math.min(force, 10);
        else if (passedMatches === 2) force = Math.min(force, 20);
        else if (passedMatches === 3) force = Math.min(force, 30);
        else if (passedMatches === 4) force = Math.min(force, 30);
        else if (passedMatches === 5) force = 40;
        else force = 0;

        return force;
    }

    ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
        const password = this.passwordToCheck;

        this.setBarColors(4, '#DDD');

        if (password) {
            const pwdStrength = this.checkStrength(password);
            pwdStrength === 40 ? this.passwordStrength.emit(true) : this.passwordStrength.emit(false);

            const color = this.getColor(pwdStrength);
            this.setBarColors(color.index, color.color);

            switch (pwdStrength) {
                case 10:
                    this.message = 'Dåligt';
                    break;
                case 20:
                    this.message = 'Inte bra';
                    break;
                case 30:
                    this.message = 'Medel';
                    break;
                case 40:
                    this.message = 'Bra';
                    break;
                default:
                    this.message = '';
                    break;
            }
        } else {
            this.message = '';
            this.checks = {
                lowerLetters: false,
                upperLetters: false,
                numbers: false,
                symbols: false,
                minLength: false
            };
            this.passwordStrength.emit(false);
        }
    }

    private getColor(strength: number) {
        let index = 0;

        if (strength === 10) {
            index = 0;
        } else if (strength === 20) {
            index = 1;
        } else if (strength === 30) {
            index = 2;
        } else if (strength === 40) {
            index = 3;
        } else {
            index = 0;
        }

        this.messageColor = this.colors[index];

        return {
            index: index + 1,
            color: this.colors[index],
        };
    }

    private setBarColors(count: number, color: string) {
        for (let n = 0; n < count; n++) {
            (this as any)['bar' + n] = color;
        }
    }
}
